import React from 'react';

/* ====================================================================
   SIGN-IN LINK SCREEN — where an emailed link lands

   "Forgot your password?", a first-time setup and claiming a profile all
   send the same kind of link, and it opens here. Signing in is the
   button; choosing a password is the option underneath. Plenty of members
   will never remember one, and asking for a link each time is a perfectly
   good way in.

   Nothing happens on arrival. Mail scanners open links to check them, and
   a link that signed in on opening would be spent before the member ever
   saw it — so it waits for a press. (The server end is magic-signin in
   server/routes/auth.js.)
   ==================================================================== */
function ResetPasswordScreen({ API, token, onSignedIn, onDone }) {
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [choosing, setChoosing] = React.useState(false);

  const finish = async (path, body) => {
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...body }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'That did not work. Ask for a new link.');
      onSignedIn(data);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const signIn = () => finish('/api/auth/magic-signin', {});

  const choosePassword = (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    finish('/api/auth/reset-password', { password });
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '24px 0' }}>
      <div className="w3-card w3-round w3-white" style={{ maxWidth: '450px', width: '100%', margin: '0 16px' }}>
        <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '16px 24px' }}>
          <h3 style={{ margin: 0 }}>
            <i className="fa fa-sign-in w3-margin-right"></i>Sign in to GreenSheet
          </h3>
        </div>

        <div className="w3-container w3-padding-24">
          {error && <div className="w3-panel w3-red w3-round w3-padding-small" role="alert"><small>{error}</small></div>}

          {!choosing ? (
            <>
              <button type="button" className="w3-button w3-theme w3-round w3-block"
                style={{ minHeight: 'var(--gs-control-lg, 52px)', fontSize: 'var(--gs-text-base)' }}
                onClick={signIn} disabled={loading}>
                {loading ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-sign-in w3-margin-right"></i>Sign me in</>}
              </button>
              <p className="w3-small w3-text-grey" style={{ margin: '16px 0 0', textAlign: 'center' }}>
                Rather have a password for next time?{' '}
                <button type="button" className="auth-forgot" onClick={() => { setChoosing(true); setError(''); }}>
                  Choose one
                </button>
              </p>
            </>
          ) : (
            <form onSubmit={choosePassword}>
              <p className="w3-small w3-text-grey" style={{ marginTop: 0, marginBottom: '16px' }}>
                Choose a password. You will be signed in straight after.
              </p>
              <label className="w3-small w3-text-grey" htmlFor="reset-password">New password</label>
              <input id="reset-password" className="w3-input w3-border w3-round w3-margin-bottom" type="password" required minLength={8}
                autoComplete="new-password"
                value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" />
              <label className="w3-small w3-text-grey" htmlFor="reset-confirm">Confirm password</label>
              <input id="reset-confirm" className="w3-input w3-border w3-round w3-margin-bottom" type="password" required minLength={8}
                autoComplete="new-password"
                value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter password" />
              <button className="w3-button w3-theme w3-round w3-block w3-margin-top" type="submit" disabled={loading}>
                {loading ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-check w3-margin-right"></i>Set password and sign in</>}
              </button>
              <p className="w3-small" style={{ margin: '12px 0 0', textAlign: 'center' }}>
                <button type="button" className="auth-forgot" onClick={() => { setChoosing(false); setError(''); }}>
                  Just sign me in instead
                </button>
              </p>
            </form>
          )}

          <p className="w3-small" style={{ margin: '20px 0 0', textAlign: 'center' }}>
            <button type="button" className="auth-forgot" onClick={onDone}>Back to the sign-in page</button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordScreen;
